<template>
  <div class="app-container income-admin-page">
    <div class="page-heading">
      <div>
        <h2>額外收入管理</h2>
        <p>管理合作方案、正式申請、收益與 Mock 請領狀態。</p>
      </div>
      <el-button
        v-if="activeTab === 'opportunities'"
        type="primary"
        icon="el-icon-plus"
        :disabled="featureDisabled"
        @click="openEditor()"
      >
        新增方案
      </el-button>
      <el-button
        v-else-if="activeTab === 'earnings'"
        type="primary"
        icon="el-icon-plus"
        :disabled="featureDisabled"
        @click="openEarningEditor()"
      >
        新增收益
      </el-button>
    </div>

    <el-alert
      title="本頁僅 ITA-DEV 與 ITA-ADMIN 可使用；請領僅變更 Mock 狀態，不取得銀行資料、不連接金流、不執行撥款。"
      type="warning"
      :closable="false"
      show-icon
      class="permission-alert"
    />

    <el-alert
      v-if="featureDisabled"
      type="warning"
      title="專區已關閉"
      description="領隊導遊專區目前已關閉，新增、狀態變更與請領處理等寫操作已停用；如需開放，請聯絡系統管理員。"
      :closable="false"
      show-icon
      class="permission-alert"
    />

    <el-tabs v-model="activeTab" @tab-click="onTabChange">
      <el-tab-pane label="方案管理" name="opportunities">
        <el-card shadow="never" class="filter-card">
          <el-form :inline="true" :model="opportunityQuery" @submit.native.prevent>
            <el-form-item label="關鍵字">
              <el-input
                v-model.trim="opportunityQuery.keyword"
                clearable
                maxlength="100"
                placeholder="標題或合作單位"
                @keyup.enter.native="searchOpportunities"
              />
            </el-form-item>
            <el-form-item label="狀態">
              <el-select v-model="opportunityQuery.status" clearable placeholder="全部">
                <el-option
                  v-for="option in opportunityStatusOptions"
                  :key="option.value"
                  :label="option.label"
                  :value="option.value"
                />
              </el-select>
            </el-form-item>
            <el-form-item>
              <el-button
                type="primary"
                icon="el-icon-search"
                :loading="opportunityLoading"
                @click="searchOpportunities"
              >
                查詢
              </el-button>
              <el-button
                icon="el-icon-refresh"
                :disabled="opportunityLoading"
                @click="resetOpportunities"
              >
                重設
              </el-button>
            </el-form-item>
          </el-form>
        </el-card>

        <el-alert
          v-if="opportunityError"
          class="load-alert"
          type="error"
          :title="opportunityError"
          :closable="false"
          show-icon
        />
        <el-table
          v-loading="opportunityLoading"
          :data="opportunities"
          border
          stripe
          empty-text="尚無額外收入方案"
        >
          <el-table-column prop="title" label="標題" min-width="220" show-overflow-tooltip />
          <el-table-column prop="partnerName" label="合作單位" min-width="150" />
          <el-table-column prop="category" label="分類" width="110" />
          <el-table-column label="名額" width="85" align="center">
            <template slot-scope="scope">
              {{ scope.row.remainingQuota == null ? '不限' : scope.row.remainingQuota }}
            </template>
          </el-table-column>
          <el-table-column prop="applicationCount" label="申請" width="75" align="center" />
          <el-table-column label="推薦" width="105" align="center">
            <template slot-scope="scope">
              <el-switch
                :value="scope.row.isRecommended === true"
                :disabled="recommendationSavingId === scope.row.id || featureDisabled"
                @change="updateRecommendation(scope.row, $event)"
              />
              <small v-if="scope.row.isRecommended" class="sort-label">
                #{{ scope.row.recommendSort }}
              </small>
            </template>
          </el-table-column>
          <el-table-column label="狀態" width="90" align="center">
            <template slot-scope="scope">
              <el-tag :type="statusTagType(scope.row.status)">
                {{ opportunityStatusLabel(scope.row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="390" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button size="mini" @click="previewOpportunity(scope.row)">預覽</el-button>
              <el-button size="mini" :disabled="featureDisabled" @click="openEditor(scope.row)">
                編輯
              </el-button>
              <el-button size="mini" :disabled="featureDisabled" @click="copyOpportunity(scope.row)">
                複製
              </el-button>
              <el-button
                v-if="['draft', 'unpublished'].includes(scope.row.status)"
                size="mini"
                type="success"
                :disabled="featureDisabled"
                @click="changeOpportunityStatus(scope.row, 'published')"
              >
                發布
              </el-button>
              <el-button
                v-if="scope.row.status === 'published'"
                size="mini"
                type="warning"
                :disabled="featureDisabled"
                @click="changeOpportunityStatus(scope.row, 'unpublished')"
              >
                下架
              </el-button>
              <el-dropdown trigger="click" @command="handleOpportunityCommand($event, scope.row)">
                <el-button size="mini"
                  >更多<i class="el-icon-arrow-down el-icon--right"
                /></el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item command="applications">申請管理</el-dropdown-item>
                  <el-dropdown-item
                    command="close"
                    :disabled="scope.row.status === 'closed' || featureDisabled"
                  >
                    結束方案
                  </el-dropdown-item>
                  <el-dropdown-item command="delete" divided :disabled="featureDisabled">
                    刪除方案
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </template>
          </el-table-column>
        </el-table>
        <pagination
          v-show="opportunityTotal > 0"
          :total="opportunityTotal"
          :page.sync="opportunityQuery.page"
          :limit.sync="opportunityQuery.limit"
          @pagination="fetchOpportunities"
        />
      </el-tab-pane>

      <el-tab-pane label="申請管理" name="applications">
        <el-card shadow="never" class="filter-card">
          <el-form :inline="true" :model="applicationQuery">
            <el-form-item label="方案">
              <el-select
                v-model="selectedOpportunityId"
                filterable
                placeholder="請選擇方案"
                class="opportunity-selector"
                @change="searchApplications"
              >
                <el-option
                  v-for="option in opportunityOptions"
                  :key="option.id"
                  :label="`${option.title}｜${option.partnerName}`"
                  :value="option.id"
                />
              </el-select>
            </el-form-item>
            <el-form-item label="狀態">
              <el-select
                v-model="applicationQuery.status"
                clearable
                placeholder="全部"
                @change="searchApplications"
              >
                <el-option
                  v-for="option in applicationStatusOptions"
                  :key="option.value"
                  :label="option.label"
                  :value="option.value"
                />
              </el-select>
            </el-form-item>
          </el-form>
          <div v-if="opportunityOptionsError" class="selector-load-error">
            <el-alert type="error" :title="opportunityOptionsError" :closable="false" show-icon />
            <el-button
              v-if="opportunityOptionsRetryable"
              size="mini"
              @click="retryOpportunityOptions"
            >
              重新載入方案
            </el-button>
          </div>
        </el-card>
        <el-alert
          v-if="applicationError"
          class="load-alert"
          type="error"
          :title="applicationError"
          :closable="false"
          show-icon
        />
        <el-table
          v-loading="applicationLoading"
          :data="applications"
          border
          stripe
          empty-text="請選擇方案，或目前尚無正式申請"
        >
          <el-table-column prop="applicantName" label="姓名" min-width="110" />
          <el-table-column prop="eligibleRole" label="身分" width="85">
            <template slot-scope="scope">{{ eligibleRoleLabel(scope.row.eligibleRole) }}</template>
          </el-table-column>
          <el-table-column prop="contact" label="聯絡方式" min-width="130" />
          <el-table-column prop="summary" label="申請摘要" min-width="230" show-overflow-tooltip />
          <el-table-column prop="submittedAt" label="申請時間" width="170" />
          <el-table-column label="狀態" width="95">
            <template slot-scope="scope">
              <el-tag :type="applicationStatusTagType(scope.row.status)">
                {{ applicationStatusLabel(scope.row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="310" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button size="mini" @click="showApplication(scope.row)">詳情</el-button>
              <el-button
                v-if="['submitted', 'reviewing'].includes(scope.row.status)"
                size="mini"
                :disabled="featureDisabled"
                @click="changeApplicationStatus(scope.row, 'reviewing')"
              >
                審核中
              </el-button>
              <el-button
                v-if="['submitted', 'reviewing'].includes(scope.row.status)"
                size="mini"
                type="success"
                :disabled="featureDisabled"
                @click="changeApplicationStatus(scope.row, 'approved')"
              >
                核准
              </el-button>
              <el-button
                v-if="['submitted', 'reviewing'].includes(scope.row.status)"
                size="mini"
                type="danger"
                :disabled="featureDisabled"
                @click="changeApplicationStatus(scope.row, 'rejected')"
              >
                拒絕
              </el-button>
              <el-button
                v-if="scope.row.status === 'approved'"
                size="mini"
                type="primary"
                :disabled="featureDisabled"
                @click="openEarningEditor(null, scope.row)"
              >
                建立收益
              </el-button>
              <el-button
                v-if="['submitted', 'reviewing', 'approved'].includes(scope.row.status)"
                size="mini"
                type="warning"
                :disabled="featureDisabled"
                @click="changeApplicationStatus(scope.row, 'cancelled')"
              >
                取消
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        <pagination
          v-show="applicationTotal > 0"
          :total="applicationTotal"
          :page.sync="applicationQuery.page"
          :limit.sync="applicationQuery.limit"
          @pagination="fetchApplications"
        />
      </el-tab-pane>

      <el-tab-pane label="收益與 Mock 請領" name="earnings">
        <el-card shadow="never" class="filter-card">
          <el-form :inline="true" :model="earningQuery" @submit.native.prevent>
            <el-form-item label="關鍵字">
              <el-input
                v-model.trim="earningQuery.keyword"
                clearable
                maxlength="100"
                placeholder="會員、方案或收益標題"
                @keyup.enter.native="searchEarnings"
              />
            </el-form-item>
            <el-form-item label="收益狀態">
              <el-select v-model="earningQuery.status" clearable placeholder="全部">
                <el-option
                  v-for="option in earningStatusOptions"
                  :key="option.value"
                  :label="option.label"
                  :value="option.value"
                />
              </el-select>
            </el-form-item>
            <el-form-item label="Mock 請領狀態">
              <el-select v-model="earningQuery.claimStatus" clearable placeholder="全部">
                <el-option
                  v-for="option in claimStatusOptions"
                  :key="option.value"
                  :label="option.label"
                  :value="option.value"
                />
              </el-select>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" :loading="earningLoading" @click="searchEarnings">
                查詢
              </el-button>
            </el-form-item>
          </el-form>
        </el-card>
        <el-alert
          v-if="earningError"
          class="load-alert"
          type="error"
          :title="earningError"
          :closable="false"
          show-icon
        />
        <el-table
          v-loading="earningLoading"
          :data="earnings"
          border
          stripe
          empty-text="尚無收益記錄"
        >
          <el-table-column prop="memberName" label="會員" width="120" />
          <el-table-column
            prop="opportunityTitle"
            label="方案"
            min-width="180"
            show-overflow-tooltip
          />
          <el-table-column prop="title" label="收益標題" min-width="160" show-overflow-tooltip />
          <el-table-column label="金額" width="130" align="right">
            <template slot-scope="scope">
              {{ formatAmount(scope.row.amount, scope.row.currency) }}
            </template>
          </el-table-column>
          <el-table-column label="收益狀態" width="105">
            <template slot-scope="scope">{{ earningStatusLabel(scope.row.status) }}</template>
          </el-table-column>
          <el-table-column label="Mock 請領" min-width="180">
            <template slot-scope="scope">
              <template v-if="scope.row.claim">
                {{ claimStatusLabel(scope.row.claim.status) }}
                <small class="time-label">{{ scope.row.claim.requestedAt }}</small>
              </template>
              <span v-else>未提出</span>
            </template>
          </el-table-column>
          <el-table-column prop="completedAt" label="完成日" width="120" />
          <el-table-column label="操作" width="290" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button
                v-if="['pending', 'available'].includes(scope.row.status)"
                size="mini"
                :disabled="featureDisabled"
                @click="openEarningEditor(scope.row)"
              >
                編輯
              </el-button>
              <template v-if="scope.row.claim && scope.row.claim.status === 'pending'">
                <el-button
                  size="mini"
                  type="success"
                  :disabled="featureDisabled"
                  @click="openClaimDecision(scope.row, 'approved')"
                >
                  核准
                </el-button>
                <el-button
                  size="mini"
                  type="danger"
                  :disabled="featureDisabled"
                  @click="openClaimDecision(scope.row, 'rejected')"
                >
                  拒絕
                </el-button>
              </template>
              <el-button
                v-if="!scope.row.claim && ['pending', 'available'].includes(scope.row.status)"
                size="mini"
                type="danger"
                :disabled="featureDisabled"
                @click="deleteEarning(scope.row)"
              >
                刪除
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        <pagination
          v-show="earningTotal > 0"
          :total="earningTotal"
          :page.sync="earningQuery.page"
          :limit.sync="earningQuery.limit"
          @pagination="fetchEarnings"
        />
      </el-tab-pane>
    </el-tabs>

    <el-dialog
      :title="editingOpportunity.id ? '編輯額外收入方案' : '新增額外收入方案'"
      :visible.sync="editorVisible"
      width="780px"
    >
      <el-form
        ref="opportunityForm"
        :model="editingOpportunity"
        :rules="opportunityRules"
        label-width="110px"
      >
        <el-form-item label="標題" prop="title">
          <el-input v-model.trim="editingOpportunity.title" maxlength="120" show-word-limit />
        </el-form-item>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="合作單位" prop="partnerName">
              <el-input v-model.trim="editingOpportunity.partnerName" maxlength="100" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="分類" prop="category">
              <el-input v-model.trim="editingOpportunity.category" maxlength="50" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="適用身分" prop="eligibleRole">
              <el-select v-model="editingOpportunity.eligibleRole" style="width: 100%">
                <el-option label="領隊" value="leader" />
                <el-option label="導遊" value="guide" />
                <el-option label="皆可" value="both" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="工作模式" prop="workMode">
              <el-input v-model.trim="editingOpportunity.workMode" maxlength="50" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="報酬說明" prop="rewardDescription">
          <el-input v-model.trim="editingOpportunity.rewardDescription" maxlength="200" />
        </el-form-item>
        <el-form-item label="摘要" prop="summary">
          <el-input
            v-model.trim="editingOpportunity.summary"
            type="textarea"
            :rows="3"
            maxlength="1000"
            show-word-limit
          />
        </el-form-item>
        <el-form-item label="工作項目" prop="workItems">
          <el-input
            v-model.trim="editingOpportunity.workItems"
            type="textarea"
            :rows="3"
            maxlength="2000"
            show-word-limit
          />
        </el-form-item>
        <el-form-item label="資格要求" prop="requirements">
          <el-input
            v-model.trim="editingOpportunity.requirements"
            type="textarea"
            :rows="3"
            maxlength="2000"
            show-word-limit
          />
        </el-form-item>
        <el-form-item label="標籤" prop="tags">
          <el-select
            v-model="editingOpportunity.tags"
            multiple
            filterable
            allow-create
            default-first-option
            :multiple-limit="8"
            placeholder="最多 8 個標籤，每個最多 30 字"
            style="width: 100%"
          />
        </el-form-item>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="名額" prop="quota">
              <el-input-number
                v-model="editingOpportunity.quota"
                :min="0"
                :max="999999"
                :precision="0"
              />
              <small class="field-hint">0 表示不限</small>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="截止日">
              <el-date-picker
                v-model="editingOpportunity.applicationDeadline"
                value-format="yyyy-MM-dd"
                style="width: 100%"
                :disabled="editingOpportunity.alwaysOpen"
              />
              <el-checkbox v-model="editingOpportunity.alwaysOpen">長期開放</el-checkbox>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="推薦">
              <el-switch v-model="editingOpportunity.isRecommended" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="推薦排序">
              <el-input-number v-model="editingOpportunity.recommendSort" :min="0" :max="9999" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <span slot="footer">
        <el-button @click="editorVisible = false">取消</el-button>
        <el-button type="primary" :loading="opportunitySaving" @click="saveOpportunity">
          儲存
        </el-button>
      </span>
    </el-dialog>

    <el-dialog title="方案預覽" :visible.sync="previewVisible" width="720px">
      <el-descriptions v-if="previewItem" :column="2" border>
        <el-descriptions-item label="標題" :span="2">{{ previewItem.title }}</el-descriptions-item>
        <el-descriptions-item label="合作單位">{{ previewItem.partnerName }}</el-descriptions-item>
        <el-descriptions-item label="分類">{{ previewItem.category }}</el-descriptions-item>
        <el-descriptions-item label="報酬" :span="2">{{
          previewItem.rewardDescription
        }}</el-descriptions-item>
        <el-descriptions-item label="摘要" :span="2">{{
          previewItem.summary
        }}</el-descriptions-item>
        <el-descriptions-item label="工作項目" :span="2">{{
          previewItem.workItems
        }}</el-descriptions-item>
        <el-descriptions-item label="資格要求" :span="2">{{
          previewItem.requirements
        }}</el-descriptions-item>
        <el-descriptions-item label="截止日">
          {{ previewItem.alwaysOpen ? '長期開放' : previewItem.applicationDeadline || '未設定' }}
        </el-descriptions-item>
        <el-descriptions-item label="標籤">
          {{ (previewItem.tags || []).join('、') || '無' }}
        </el-descriptions-item>
      </el-descriptions>
    </el-dialog>

    <el-dialog title="申請詳情" :visible.sync="applicationDetailVisible" width="720px">
      <el-descriptions v-if="applicationDetail" :column="2" border>
        <el-descriptions-item label="姓名">{{
          applicationDetail.applicantName
        }}</el-descriptions-item>
        <el-descriptions-item label="身分">
          {{ eligibleRoleLabel(applicationDetail.eligibleRole) }}
        </el-descriptions-item>
        <el-descriptions-item label="聯絡方式" :span="2">
          {{ applicationDetail.contact }}
        </el-descriptions-item>
        <el-descriptions-item label="相關經驗" :span="2">
          {{ applicationDetail.experience }}
        </el-descriptions-item>
        <el-descriptions-item label="申請原因" :span="2">{{
          applicationDetail.reason
        }}</el-descriptions-item>
        <el-descriptions-item label="可配合時間" :span="2">
          {{ applicationDetail.availability }}
        </el-descriptions-item>
        <el-descriptions-item label="作品連結" :span="2">
          {{ applicationDetail.portfolioUrl || '無' }}
        </el-descriptions-item>
        <el-descriptions-item label="補充說明" :span="2">{{
          applicationDetail.extra
        }}</el-descriptions-item>
      </el-descriptions>
    </el-dialog>

    <el-dialog
      :title="editingEarning.id ? '編輯收益記錄' : '新增收益記錄'"
      :visible.sync="earningEditorVisible"
      width="720px"
    >
      <el-form ref="earningForm" :model="editingEarning" :rules="earningRules" label-width="110px">
        <el-form-item label="合作方案" prop="opportunityId">
          <el-select
            v-model="editingEarning.opportunityId"
            filterable
            :disabled="Boolean(editingEarning.id)"
            style="width: 100%"
            @change="loadApprovedApplications"
          >
            <el-option
              v-for="option in opportunityOptions"
              :key="option.id"
              :label="`${option.title}｜${option.partnerName}`"
              :value="option.id"
            />
          </el-select>
          <div v-if="opportunityOptionsError" class="selector-load-error">
            <el-alert type="error" :title="opportunityOptionsError" :closable="false" show-icon />
            <el-button
              v-if="opportunityOptionsRetryable"
              size="mini"
              @click="retryOpportunityOptions"
            >
              重新載入方案
            </el-button>
          </div>
        </el-form-item>
        <el-form-item v-if="!editingEarning.id" label="核准申請" prop="applicationId">
          <el-select
            v-model="editingEarning.applicationId"
            filterable
            :loading="approvedApplicationsLoading"
            style="width: 100%"
            @change="selectEarningApplication"
          >
            <el-option
              v-for="application in approvedApplications"
              :key="application.id"
              :label="`${application.applicantName}｜${application.summary || ''}`"
              :value="application.id"
            />
          </el-select>
          <div v-if="approvedApplicationsError" class="selector-load-error">
            <el-alert type="error" :title="approvedApplicationsError" :closable="false" show-icon />
            <el-button
              v-if="approvedApplicationsRetryable"
              size="mini"
              @click="retryApprovedApplications"
            >
              重新載入核准申請
            </el-button>
          </div>
        </el-form-item>
        <el-form-item label="會員">
          <el-input :value="editingEarning.memberName || editingEarning.userId" disabled />
        </el-form-item>
        <el-form-item label="收益標題" prop="title">
          <el-input v-model.trim="editingEarning.title" maxlength="200" show-word-limit />
        </el-form-item>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="金額" prop="amount">
              <el-input-number
                v-model="editingEarning.amount"
                :min="0.01"
                :max="9999999999.99"
                :precision="2"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="幣別" prop="currency">
              <el-input v-model.trim="editingEarning.currency" maxlength="3" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="收益狀態" prop="status">
              <el-select v-model="editingEarning.status" style="width: 100%">
                <el-option label="待確認" value="pending" />
                <el-option label="可請領" value="available" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="完成日" prop="completedAt">
              <el-date-picker
                v-model="editingEarning.completedAt"
                type="date"
                value-format="yyyy-MM-dd"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="結算日">
          <el-date-picker
            v-model="editingEarning.settlementDate"
            type="date"
            value-format="yyyy-MM-dd"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="說明">
          <el-input
            v-model.trim="editingEarning.description"
            type="textarea"
            :rows="3"
            maxlength="1000"
            show-word-limit
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="earningEditorVisible = false">取消</el-button>
        <el-button type="primary" :loading="earningSaving" @click="saveEarning">儲存</el-button>
      </span>
    </el-dialog>

    <el-dialog
      :title="claimDecision.status === 'approved' ? '核准 Mock 請領' : '拒絕 Mock 請領'"
      :visible.sync="claimDecision.visible"
      width="520px"
    >
      <el-alert
        title="此操作只會記錄 Mock 狀態與稽核資訊，不會執行真實撥款。"
        type="warning"
        :closable="false"
        show-icon
      />
      <el-form label-width="80px" class="claim-form">
        <el-form-item label="處理說明">
          <el-input
            v-model.trim="claimDecision.note"
            type="textarea"
            :rows="4"
            maxlength="500"
            show-word-limit
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="claimDecision.visible = false">取消</el-button>
        <el-button type="primary" :loading="claimDecision.saving" @click="submitClaimDecision">
          確認
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import Pagination from '@/components/Pagination'
import {
  copyIncomeOpportunity,
  createIncomeEarning,
  createIncomeOpportunity,
  deleteIncomeEarning,
  deleteIncomeOpportunity,
  getIncomeApplications,
  getIncomeEarnings,
  getIncomeOpportunity,
  getIncomeOpportunities,
  updateIncomeApplicationStatus,
  updateIncomeEarning,
  updateIncomeEarningClaimStatus,
  updateIncomeOpportunity,
  updateIncomeOpportunityRecommendation,
  updateIncomeOpportunityStatus,
} from '@/api/tourLeaderAdmin'
import { requireSuccessfulResponse, toPagedResult } from '@/utils/tourLeaderApi'
import { isTourLeaderFeatureDisabled } from '@/utils/tourLeaderPhaseSeven'
import { hasAnyRole, TOUR_LEADER_CONTENT_ADMIN_ROLES } from '@/utils/tourLeaderPermissions'

function newOpportunity() {
  return {
    id: null,
    title: '',
    partnerName: '',
    category: '',
    eligibleRole: 'both',
    workMode: '',
    rewardDescription: '',
    summary: '',
    workItems: '',
    requirements: '',
    quota: 0,
    applicationDeadline: '',
    alwaysOpen: false,
    tags: [],
    isRecommended: false,
    recommendSort: 99,
  }
}

function newEarning() {
  return {
    id: null,
    opportunityId: '',
    applicationId: '',
    userId: null,
    memberName: '',
    title: '',
    amount: null,
    currency: 'TWD',
    status: 'pending',
    description: '',
    completedAt: '',
    settlementDate: '',
  }
}

function clone(source) {
  return JSON.parse(JSON.stringify(source))
}

function validateQuota(rule, value, callback) {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 0 || value > 999999) {
    callback(new Error('名額必須是 0 至 999999 的整數'))
    return
  }
  callback()
}

function validateTags(rule, value, callback) {
  if (!Array.isArray(value) || value.length > 8) {
    callback(new Error('標籤最多 8 個'))
    return
  }
  if (value.some(tag => String(tag).trim().length < 1 || String(tag).trim().length > 30)) {
    callback(new Error('每個標籤必須為 1 至 30 字'))
    return
  }
  callback()
}

const selectorPageSize = 100
const maxSelectorPages = 100
const selectorPagingStalled = 'tour_leader_income_selector_paging_stalled'
const selectorPageLimitExceeded = 'tour_leader_income_selector_page_limit_exceeded'

function describeSelectorLoadError(error, subject) {
  const code = error && error.message
  if (code === selectorPageLimitExceeded) {
    return {
      code,
      retryable: false,
      message: `因系統限制，${subject}選單最多載入 ${(
        selectorPageSize * maxSelectorPages
      ).toLocaleString()} 筆；請聯絡系統管理員縮小可用資料範圍。`,
    }
  }
  if (code === selectorPagingStalled) {
    return {
      code,
      retryable: true,
      message: `${subject}分頁資料不完整，請按「重新載入」再試一次。`,
    }
  }
  return {
    code: 'tour_leader_income_selector_load_failed',
    retryable: true,
    message: `${subject}載入失敗，請按「重新載入」再試一次。`,
  }
}

async function collectSelectorPages(fetchPage, isCurrent) {
  const collected = []
  const seenIds = new Set()

  const appendPage = (items, page) => {
    let added = 0
    items.forEach((item, index) => {
      const key = item && item.id != null ? String(item.id) : `missing-id:${page}:${index}`
      if (seenIds.has(key)) return
      seenIds.add(key)
      collected.push(item)
      added += 1
    })
    return added
  }

  const firstPage = await fetchPage(1, selectorPageSize)
  if (!isCurrent()) return null
  const firstResult = toPagedResult(firstPage)
  appendPage(firstResult.items, 1)
  const expectedTotal = Math.max(firstResult.total, firstResult.items.length)
  if (expectedTotal > 0 && firstResult.items.length === 0) {
    throw new Error(selectorPagingStalled)
  }
  const pageCount = Math.ceil(expectedTotal / selectorPageSize)
  if (pageCount > maxSelectorPages) {
    throw new Error(selectorPageLimitExceeded)
  }

  for (let page = 2; page <= pageCount; page += 1) {
    const response = await fetchPage(page, selectorPageSize)
    if (!isCurrent()) return null
    const result = toPagedResult(response)
    if (!result.items.length || appendPage(result.items, page) === 0) {
      throw new Error(selectorPagingStalled)
    }
  }

  if (collected.length < expectedTotal) {
    throw new Error(selectorPagingStalled)
  }

  return collected
}

export default {
  name: 'TourLeaderIncomeIndex',
  components: { Pagination },
  data() {
    return {
      activeTab: 'opportunities',
      opportunityQuery: { keyword: '', status: '', page: 1, limit: 20 },
      featureDisabled: false,
      opportunities: [],
      opportunityOptions: [],
      opportunityOptionsRequestId: 0,
      opportunityOptionsError: '',
      opportunityOptionsErrorCode: '',
      opportunityOptionsRetryable: false,
      opportunityTotal: 0,
      opportunityLoading: false,
      opportunityError: '',
      opportunitySaving: false,
      recommendationSavingId: null,
      editorVisible: false,
      editingOpportunity: newOpportunity(),
      previewVisible: false,
      previewItem: null,
      selectedOpportunityId: '',
      applicationQuery: { status: '', page: 1, limit: 20 },
      applications: [],
      applicationTotal: 0,
      applicationLoading: false,
      applicationError: '',
      applicationRequestId: 0,
      applicationDetailVisible: false,
      applicationDetail: null,
      earningQuery: { keyword: '', status: '', claimStatus: '', page: 1, limit: 20 },
      earnings: [],
      earningTotal: 0,
      earningLoading: false,
      earningError: '',
      earningEditorVisible: false,
      editingEarning: newEarning(),
      earningSaving: false,
      approvedApplications: [],
      approvedApplicationsOpportunityId: '',
      approvedApplicationsLoading: false,
      approvedApplicationsRequestId: 0,
      approvedApplicationsError: '',
      approvedApplicationsErrorCode: '',
      approvedApplicationsRetryable: false,
      claimDecision: { visible: false, saving: false, earning: null, status: '', note: '' },
      opportunityStatusOptions: [
        { label: '草稿', value: 'draft' },
        { label: '已發布', value: 'published' },
        { label: '已下架', value: 'unpublished' },
        { label: '已結束', value: 'closed' },
      ],
      applicationStatusOptions: [
        { label: '已送出', value: 'submitted' },
        { label: '審核中', value: 'reviewing' },
        { label: '已核准', value: 'approved' },
        { label: '已拒絕', value: 'rejected' },
        { label: '已取消', value: 'cancelled' },
      ],
      earningStatusOptions: [
        { label: '待確認', value: 'pending' },
        { label: '可請領', value: 'available' },
        { label: '請領處理中', value: 'claimPending' },
        { label: '已請領', value: 'claimed' },
      ],
      claimStatusOptions: [
        { label: '待處理', value: 'pending' },
        { label: '已核准', value: 'approved' },
        { label: '已拒絕', value: 'rejected' },
      ],
      opportunityRules: {
        title: [{ required: true, message: '請輸入標題', trigger: 'blur' }],
        partnerName: [{ required: true, message: '請輸入合作單位', trigger: 'blur' }],
        category: [{ required: true, message: '請輸入分類', trigger: 'blur' }],
        eligibleRole: [{ required: true, message: '請選擇適用身分', trigger: 'change' }],
        workMode: [{ required: true, message: '請輸入工作模式', trigger: 'blur' }],
        rewardDescription: [{ required: true, message: '請輸入報酬說明', trigger: 'blur' }],
        summary: [{ required: true, message: '請輸入摘要', trigger: 'blur' }],
        quota: [{ validator: validateQuota, trigger: 'change' }],
        tags: [{ validator: validateTags, trigger: 'change' }],
      },
      earningRules: {
        opportunityId: [{ required: true, message: '請選擇合作方案', trigger: 'change' }],
        applicationId: [{ required: true, message: '請選擇已核准申請', trigger: 'change' }],
        title: [{ required: true, message: '請輸入收益標題', trigger: 'blur' }],
        amount: [{ required: true, message: '請輸入金額', trigger: 'change' }],
        currency: [{ required: true, message: '請輸入幣別', trigger: 'blur' }],
        status: [{ required: true, message: '請選擇收益狀態', trigger: 'change' }],
      },
    }
  },
  computed: {
    canManageIncome() {
      return hasAnyRole(this.$store.getters.roles, TOUR_LEADER_CONTENT_ADMIN_ROLES)
    },
  },
  created() {
    if (!this.canManageIncome) {
      this.$router.replace('/401')
      return
    }
    this.fetchOpportunities()
    this.fetchOpportunityOptions()
  },
  methods: {
    ensureAuthorized() {
      if (this.canManageIncome) return true
      this.$router.replace('/401')
      return false
    },
    handleFeatureDisabled(error) {
      if (!isTourLeaderFeatureDisabled(error)) return false
      this.featureDisabled = true
      this.opportunities = []
      this.opportunityTotal = 0
      this.opportunityError = ''
      this.opportunityLoading = false
      this.applications = []
      this.applicationTotal = 0
      this.applicationError = ''
      this.applicationLoading = false
      this.earnings = []
      this.earningTotal = 0
      this.earningError = ''
      this.earningLoading = false
      this.editorVisible = false
      this.earningEditorVisible = false
      return true
    },
    sessionFingerprint() {
      const getters = this.$store.getters || {}
      const userInfo = getters.userInfo || {}
      const roles = Array.isArray(getters.roles) ? [...getters.roles].sort() : []
      return JSON.stringify([
        getters.token || '',
        getters.account || '',
        userInfo.id || userInfo.ID || userInfo.userId || '',
        roles,
      ])
    },
    async onTabChange() {
      if (!this.ensureAuthorized()) return
      if (this.activeTab === 'applications' && this.selectedOpportunityId) {
        await this.fetchApplications()
      } else if (this.activeTab === 'earnings') {
        await this.fetchEarnings()
      }
    },
    async fetchOpportunities() {
      if (!this.ensureAuthorized()) return
      this.opportunityLoading = true
      this.opportunityError = ''
      try {
        const result = toPagedResult(await getIncomeOpportunities(this.opportunityQuery))
        this.opportunities = result.items
        this.opportunityTotal = result.total
        this.featureDisabled = false
      } catch (error) {
        this.opportunities = []
        this.opportunityTotal = 0
        if (!this.handleFeatureDisabled(error)) {
          this.opportunityError = '無法載入額外收入方案。'
        }
      } finally {
        this.opportunityLoading = false
      }
    },
    async fetchOpportunityOptions() {
      if (!this.ensureAuthorized()) return
      const requestId = ++this.opportunityOptionsRequestId
      const session = this.sessionFingerprint()
      const isCurrent = () =>
        requestId === this.opportunityOptionsRequestId &&
        this.canManageIncome &&
        session === this.sessionFingerprint()
      this.opportunityOptionsError = ''
      this.opportunityOptionsErrorCode = ''
      this.opportunityOptionsRetryable = false
      try {
        const items = await collectSelectorPages(
          (page, pageSize) => getIncomeOpportunities({ page, pageSize }),
          isCurrent
        )
        if (items && isCurrent()) this.opportunityOptions = items
      } catch (error) {
        if (isCurrent() && this.handleFeatureDisabled(error)) return
        if (isCurrent()) {
          const failure = describeSelectorLoadError(error, '方案')
          this.opportunityOptionsError = failure.message
          this.opportunityOptionsErrorCode = failure.code
          this.opportunityOptionsRetryable = failure.retryable
        }
      }
    },
    retryOpportunityOptions() {
      if (!this.opportunityOptionsRetryable) return
      return this.fetchOpportunityOptions()
    },
    searchOpportunities() {
      this.opportunityQuery.page = 1
      return this.fetchOpportunities()
    },
    resetOpportunities() {
      this.opportunityQuery = { keyword: '', status: '', page: 1, limit: 20 }
      return this.fetchOpportunities()
    },
    openEditor(opportunity) {
      if (this.featureDisabled) return false
      if (!this.ensureAuthorized()) return
      this.editingOpportunity = opportunity
        ? Object.assign(newOpportunity(), clone(opportunity))
        : newOpportunity()
      this.editorVisible = true
      this.$nextTick(() => {
        if (this.$refs.opportunityForm && this.$refs.opportunityForm.clearValidate) {
          this.$refs.opportunityForm.clearValidate()
        }
      })
    },
    saveOpportunity() {
      if (this.featureDisabled) return false
      if (!this.ensureAuthorized() || !this.$refs.opportunityForm) return
      this.$refs.opportunityForm.validate(async valid => {
        if (!valid || !this.ensureAuthorized()) return
        this.opportunitySaving = true
        try {
          const payload = clone(this.editingOpportunity)
          if (payload.alwaysOpen) payload.applicationDeadline = ''
          const response = payload.id
            ? await updateIncomeOpportunity(payload.id, payload)
            : await createIncomeOpportunity(payload)
          requireSuccessfulResponse(response)
          this.$message.success('額外收入方案已儲存')
          this.editorVisible = false
          await Promise.all([this.fetchOpportunities(), this.fetchOpportunityOptions()])
        } catch (error) {
          this.$message.error('儲存失敗，請確認欄位內容。')
        } finally {
          this.opportunitySaving = false
        }
      })
    },
    async previewOpportunity(opportunity) {
      if (!this.ensureAuthorized()) return
      try {
        this.previewItem = requireSuccessfulResponse(await getIncomeOpportunity(opportunity.id))
        this.previewVisible = true
      } catch (error) {
        this.$message.error('無法載入方案預覽。')
      }
    },
    async copyOpportunity(opportunity) {
      if (this.featureDisabled) return false
      if (!this.ensureAuthorized()) return
      try {
        await this.$confirm(`確定複製「${opportunity.title}」？`, '複製方案', { type: 'warning' })
        requireSuccessfulResponse(await copyIncomeOpportunity(opportunity.id))
        this.$message.success('方案已複製為草稿')
        await Promise.all([this.fetchOpportunities(), this.fetchOpportunityOptions()])
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') this.$message.error('複製方案失敗。')
      }
    },
    async deleteOpportunity(opportunity) {
      if (this.featureDisabled) return false
      if (!this.ensureAuthorized()) return
      try {
        await this.$confirm(
          `確定刪除「${opportunity.title}」？此操作會保留稽核軌跡。`,
          '刪除方案',
          {
            type: 'warning',
          }
        )
        requireSuccessfulResponse(await deleteIncomeOpportunity(opportunity.id))
        this.$message.success('方案已刪除')
        await Promise.all([this.fetchOpportunities(), this.fetchOpportunityOptions()])
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') this.$message.error('刪除方案失敗。')
      }
    },
    async changeOpportunityStatus(opportunity, status) {
      if (this.featureDisabled) return false
      if (!this.ensureAuthorized()) return
      try {
        await this.$confirm(
          `確定要將「${opportunity.title}」設為${this.opportunityStatusLabel(status)}？`,
          '確認操作',
          { type: 'warning' }
        )
        requireSuccessfulResponse(await updateIncomeOpportunityStatus(opportunity.id, status))
        this.$message.success('方案狀態已更新')
        await this.fetchOpportunities()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') this.$message.error('狀態更新失敗。')
      }
    },
    async updateRecommendation(opportunity, value) {
      if (this.featureDisabled) return false
      if (!this.ensureAuthorized()) return
      this.recommendationSavingId = opportunity.id
      try {
        requireSuccessfulResponse(
          await updateIncomeOpportunityRecommendation(
            opportunity.id,
            value,
            opportunity.recommendSort
          )
        )
        opportunity.isRecommended = value
        this.$message.success('推薦設定已更新')
      } catch (error) {
        this.$message.error('推薦設定更新失敗。')
      } finally {
        this.recommendationSavingId = null
      }
    },
    handleOpportunityCommand(command, opportunity) {
      if (this.featureDisabled) return false
      if (command === 'applications') this.openApplications(opportunity)
      else if (command === 'close') this.changeOpportunityStatus(opportunity, 'closed')
      else if (command === 'delete') this.deleteOpportunity(opportunity)
    },
    openApplications(opportunity) {
      if (!this.ensureAuthorized()) return
      this.selectedOpportunityId = opportunity.id
      this.applicationQuery.page = 1
      this.activeTab = 'applications'
      return this.fetchApplications()
    },
    searchApplications() {
      this.applicationQuery.page = 1
      return this.fetchApplications()
    },
    async fetchApplications() {
      const requestId = ++this.applicationRequestId
      if (!this.ensureAuthorized() || !this.selectedOpportunityId) {
        if (requestId === this.applicationRequestId) {
          this.applications = []
          this.applicationTotal = 0
          this.applicationError = ''
          this.applicationLoading = false
        }
        return
      }
      const context = {
        opportunityId: this.selectedOpportunityId,
        status: this.applicationQuery.status,
        page: this.applicationQuery.page,
        limit: this.applicationQuery.limit,
        session: this.sessionFingerprint(),
      }
      const isCurrent = () =>
        requestId === this.applicationRequestId &&
        this.canManageIncome &&
        context.opportunityId === this.selectedOpportunityId &&
        context.status === this.applicationQuery.status &&
        context.page === this.applicationQuery.page &&
        context.limit === this.applicationQuery.limit &&
        context.session === this.sessionFingerprint()
      this.applicationLoading = true
      this.applicationError = ''
      try {
        const result = toPagedResult(
          await getIncomeApplications(context.opportunityId, {
            status: context.status,
            page: context.page,
            limit: context.limit,
          })
        )
        if (!isCurrent()) return
        this.applications = result.items
        this.applicationTotal = result.total
      } catch (error) {
        if (!isCurrent()) return
        if (!this.handleFeatureDisabled(error)) {
          this.applications = []
          this.applicationTotal = 0
          this.applicationError = '無法載入申請資料。'
        }
      } finally {
        if (isCurrent()) this.applicationLoading = false
      }
    },
    showApplication(application) {
      if (!this.ensureAuthorized()) return
      this.applicationDetail = clone(application)
      this.applicationDetailVisible = true
    },
    async changeApplicationStatus(application, status) {
      if (this.featureDisabled) return false
      if (!this.ensureAuthorized() || !this.selectedOpportunityId) return
      try {
        await this.$confirm(
          `確定要將 ${application.applicantName} 的申請設為${this.applicationStatusLabel(status)}？`,
          '確認操作',
          { type: 'warning' }
        )
        requireSuccessfulResponse(
          await updateIncomeApplicationStatus(this.selectedOpportunityId, application.id, status)
        )
        this.$message.success('申請狀態已更新')
        await this.fetchApplications()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') this.$message.error('申請狀態更新失敗。')
      }
    },
    searchEarnings() {
      this.earningQuery.page = 1
      return this.fetchEarnings()
    },
    async fetchEarnings() {
      if (!this.ensureAuthorized()) return
      this.earningLoading = true
      this.earningError = ''
      try {
        const result = toPagedResult(await getIncomeEarnings(this.earningQuery))
        this.earnings = result.items
        this.earningTotal = result.total
        this.featureDisabled = false
      } catch (error) {
        this.earnings = []
        this.earningTotal = 0
        if (!this.handleFeatureDisabled(error)) {
          this.earningError = '無法載入收益資料。'
        }
      } finally {
        this.earningLoading = false
      }
    },
    async openEarningEditor(earning, application) {
      if (this.featureDisabled) return false
      if (!this.ensureAuthorized()) return
      this.editingEarning = earning ? Object.assign(newEarning(), clone(earning)) : newEarning()
      if (earning) {
        this.editingEarning.completedAt = String(earning.completedAt || '').slice(0, 10)
        this.editingEarning.settlementDate = String(earning.settlementDate || '').slice(0, 10)
      }
      this.approvedApplications = []
      this.approvedApplicationsOpportunityId = ''
      this.approvedApplicationsError = ''
      this.approvedApplicationsErrorCode = ''
      this.approvedApplicationsRetryable = false
      if (application) {
        this.editingEarning.opportunityId = application.opportunityId || this.selectedOpportunityId
        this.editingEarning.applicationId = application.id
        this.editingEarning.userId = application.userId
        this.editingEarning.memberName = application.applicantName
        this.editingEarning.title = application.opportunityTitle || ''
        this.approvedApplications = [application]
        this.approvedApplicationsOpportunityId = this.editingEarning.opportunityId
      }
      this.earningEditorVisible = true
      if (!earning && this.editingEarning.opportunityId && !application) {
        await this.loadApprovedApplications(this.editingEarning.opportunityId)
      }
      this.$nextTick(() => {
        if (this.$refs.earningForm && this.$refs.earningForm.clearValidate) {
          this.$refs.earningForm.clearValidate()
        }
      })
    },
    async loadApprovedApplications(opportunityId, options = {}) {
      if (!this.ensureAuthorized()) return
      const requestId = ++this.approvedApplicationsRequestId
      const session = this.sessionFingerprint()
      const isCurrent = () =>
        requestId === this.approvedApplicationsRequestId &&
        this.canManageIncome &&
        session === this.sessionFingerprint()
      const sameOpportunity = this.approvedApplicationsOpportunityId === opportunityId
      if (!sameOpportunity) this.approvedApplications = []
      this.approvedApplicationsOpportunityId = opportunityId
      this.approvedApplicationsError = ''
      this.approvedApplicationsErrorCode = ''
      this.approvedApplicationsRetryable = false
      if (!options.preserveSelection) {
        this.editingEarning.applicationId = ''
        this.editingEarning.userId = null
        this.editingEarning.memberName = ''
      }
      this.approvedApplicationsLoading = true
      try {
        const items = await collectSelectorPages(
          (page, pageSize) =>
            getIncomeApplications(opportunityId, {
              status: 'approved',
              page,
              pageSize,
            }),
          isCurrent
        )
        if (items && isCurrent()) {
          this.approvedApplications = items.filter(item => item.status === 'approved')
        }
      } catch (error) {
        if (isCurrent()) {
          const failure = describeSelectorLoadError(error, '核准申請')
          this.approvedApplicationsError = failure.message
          this.approvedApplicationsErrorCode = failure.code
          this.approvedApplicationsRetryable = failure.retryable
        }
      } finally {
        if (requestId === this.approvedApplicationsRequestId) {
          this.approvedApplicationsLoading = false
        }
      }
    },
    retryApprovedApplications() {
      if (!this.approvedApplicationsRetryable || !this.editingEarning.opportunityId) return
      return this.loadApprovedApplications(this.editingEarning.opportunityId, {
        preserveSelection: true,
      })
    },
    selectEarningApplication(applicationId) {
      const application = this.approvedApplications.find(item => item.id === applicationId)
      if (!application) return
      this.editingEarning.userId = application.userId
      this.editingEarning.memberName = application.applicantName
      if (!this.editingEarning.title) {
        this.editingEarning.title = application.opportunityTitle || ''
      }
    },
    saveEarning() {
      if (this.featureDisabled) return false
      if (!this.ensureAuthorized() || !this.$refs.earningForm) return
      this.$refs.earningForm.validate(async valid => {
        if (!valid || !this.ensureAuthorized()) return
        this.earningSaving = true
        try {
          const earning = clone(this.editingEarning)
          earning.completedAt = String(earning.completedAt || '').trim() || null
          const response = earning.id
            ? await updateIncomeEarning(earning.id, earning)
            : await createIncomeEarning(earning.opportunityId, earning)
          requireSuccessfulResponse(response)
          this.$message.success('收益記錄已儲存')
          this.earningEditorVisible = false
          this.activeTab = 'earnings'
          await this.fetchEarnings()
        } catch (error) {
          this.$message.error('儲存收益記錄失敗，請確認欄位內容。')
        } finally {
          this.earningSaving = false
        }
      })
    },
    async deleteEarning(earning) {
      if (this.featureDisabled) return false
      if (
        !this.ensureAuthorized() ||
        earning.claim ||
        !['pending', 'available'].includes(earning.status)
      ) {
        return
      }
      try {
        await this.$confirm(
          `確定刪除「${earning.title}」收益記錄？系統會保留稽核軌跡。`,
          '刪除收益記錄',
          { type: 'warning' }
        )
        requireSuccessfulResponse(await deleteIncomeEarning(earning.id))
        this.$message.success('收益記錄已刪除')
        await this.fetchEarnings()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error('刪除收益記錄失敗。')
        }
      }
    },
    openClaimDecision(earning, status) {
      if (this.featureDisabled) return false
      if (!this.ensureAuthorized() || !earning.claim || earning.claim.status !== 'pending') return
      this.claimDecision = { visible: true, saving: false, earning, status, note: '' }
    },
    async submitClaimDecision() {
      if (this.featureDisabled) return false
      if (!this.ensureAuthorized() || !this.claimDecision.earning) return
      const noteLength = this.claimDecision.note.trim().length
      if (noteLength < 2 || noteLength > 500) {
        this.$message.error('處理說明必須為 2 至 500 字。')
        return
      }
      this.claimDecision.saving = true
      try {
        requireSuccessfulResponse(
          await updateIncomeEarningClaimStatus(
            this.claimDecision.earning.id,
            this.claimDecision.status,
            this.claimDecision.note
          )
        )
        this.$message.success('Mock 請領狀態已更新，未執行撥款')
        this.claimDecision.visible = false
        await this.fetchEarnings()
      } catch (error) {
        this.$message.error('Mock 請領狀態更新失敗。')
      } finally {
        this.claimDecision.saving = false
      }
    },
    opportunityStatusLabel(status) {
      const option = this.opportunityStatusOptions.find(item => item.value === status)
      return option ? option.label : status || '未知'
    },
    applicationStatusLabel(status) {
      const option = this.applicationStatusOptions.find(item => item.value === status)
      return option ? option.label : status || '未知'
    },
    earningStatusLabel(status) {
      const option = this.earningStatusOptions.find(item => item.value === status)
      return option ? option.label : status || '未知'
    },
    claimStatusLabel(status) {
      return { pending: '待處理', approved: '已核准', rejected: '已拒絕' }[status] || status
    },
    eligibleRoleLabel(role) {
      return { leader: '領隊', guide: '導遊', both: '皆可' }[role] || role
    },
    statusTagType(status) {
      return { draft: 'info', published: 'success', unpublished: 'warning', closed: 'danger' }[
        status
      ]
    },
    applicationStatusTagType(status) {
      return {
        submitted: 'info',
        reviewing: 'warning',
        approved: 'success',
        rejected: 'danger',
        cancelled: 'info',
      }[status]
    },
    formatAmount(amount, currency) {
      const numericAmount = Number(amount)
      const formatted = Number.isFinite(numericAmount)
        ? numericAmount.toLocaleString('zh-TW', { maximumFractionDigits: 2 })
        : '0'
      return `${String(currency || 'TWD').toUpperCase()} ${formatted}`
    },
  },
}
</script>

<style lang="scss" scoped>
.income-admin-page {
  .page-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 18px;

    h2 {
      margin: 0 0 6px;
    }

    p {
      margin: 0;
      color: #606266;
    }
  }

  .permission-alert,
  .filter-card,
  .load-alert {
    margin-bottom: 16px;
  }

  .opportunity-selector {
    width: 360px;
  }

  .selector-load-error {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;

    .el-alert {
      flex: 1;
    }
  }

  .sort-label,
  .time-label,
  .field-hint {
    display: block;
    margin-top: 4px;
    color: #909399;
  }

  .claim-form {
    margin-top: 18px;
  }
}
</style>
